import React, { useState, useEffect } from 'react';
import { User, RegisterRequest } from '../../types';
import { authService } from '../../services/api';
import UserForm from '../../components/UserForm';

const UsersManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [saveLoading, setSaveLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const userData = await authService.getUsersForLogin();
      setUsers(userData);
    } catch (err: any) {
      setError('ユーザーデータの取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (userData: RegisterRequest | FormData) => {
    setSaveLoading(true);
    try {
      await authService.register(userData);
      setShowCreateForm(false);
      fetchUsers();
    } catch (err: any) {
      const message = err.response?.data?.detail || 'ユーザーの作成に失敗しました';
      setError(message);
      throw new Error(message);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleUpdateUser = async (userData: Partial<RegisterRequest>) => {
    if (!editingUser) return;

    setSaveLoading(true);
    try {
      await authService.updateUser(editingUser.id, userData);
      setEditingUser(null);
      setError(''); // 成功時はエラーをクリア
      fetchUsers();
    } catch (err: any) {
      console.error('User update error:', err);
      const message = err.response?.data?.detail || 'ユーザーの更新に失敗しました';
      setError(message);
      throw new Error(message); // UserFormでエラーをキャッチできるようにthrowを復活
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDeleteUser = async (userId: string, userName: string) => {
    if (!window.confirm(`ユーザー "${userName}" を削除してもよろしいですか？\nこの操作は取り消せません。`)) {
      return;
    }

    setSaveLoading(true);
    try {
      await authService.deleteUser(userId);
      fetchUsers();
    } catch (err: any) {
      const message = err.response?.data?.detail || 'ユーザーの削除に失敗しました';
      setError(message);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('bizcard_access_token');
    localStorage.removeItem('bizcard_refresh_token');
    window.location.href = '/login';
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        読み込み中...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '50px', color: 'red' }}>
        {error}
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      {/* ヘッダーメニュー */}
      <div style={{
        backgroundColor: 'white',
        borderBottom: '1px solid #dee2e6',
        padding: '10px 0',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0 20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h1 style={{ margin: 0, color: '#007bff', fontSize: '24px' }}>
              名刺管理システム
            </h1>
            <span style={{ color: '#666', fontSize: '18px' }}>- ユーザー管理</span>
          </div>

          {/* メニューボタン */}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button
              onClick={() => window.location.href = '/cards'}
              style={{
                padding: '8px 16px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              名刺一覧に戻る
            </button>
            <button
              onClick={() => setShowCreateForm(true)}
              style={{
                padding: '8px 16px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              + 新規ユーザー
            </button>
            <button
              onClick={handleLogout}
              style={{
                padding: '8px 16px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              ログアウト
            </button>
          </div>
        </div>
      </div>

      {/* メインコンテンツ */}
      <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
        {users.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '50px',
            backgroundColor: 'white',
            borderRadius: '8px',
            border: '1px dashed #ddd'
          }}>
            <p style={{ color: '#666', fontSize: '18px' }}>ユーザーが登録されていません</p>
          </div>
        ) : (
          <div style={{
            backgroundColor: 'white',
            border: '1px solid #ddd',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                    アバター
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                    ユーザーID
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                    表示名
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                    メールアドレス
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                    部署
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                    ヒント
                  </th>
                  <th style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #ddd' }}>
                    ステータス
                  </th>
                  <th style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #ddd', width: '120px' }}>
                    操作
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '12px' }}>
                      {user.avatar_url ? (
                        <img
                          src={user.avatar_url}
                          alt={user.display_name || user.email}
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            backgroundColor: '#e9ecef',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            color: '#6c757d'
                          }}
                        >
                          {(user.display_name || user.username).charAt(0).toUpperCase()}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '12px', fontWeight: 'bold' }}>
                      {user.username}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {user.display_name || '-'}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {user.email || '-'}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {user.department || '-'}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {user.hint || '-'}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <span
                        style={{
                          padding: '3px 8px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          backgroundColor: user.is_active ? '#d4edda' : '#f8d7da',
                          color: user.is_active ? '#155724' : '#721c24'
                        }}
                      >
                        {user.is_active ? 'アクティブ' : '無効'}
                      </span>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                        <button
                          onClick={() => setEditingUser(user)}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          編集
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user.id, user.username)}
                          disabled={saveLoading}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: saveLoading ? 'not-allowed' : 'pointer',
                            fontSize: '12px',
                            opacity: saveLoading ? 0.6 : 1
                          }}
                        >
                          削除
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* 新規ユーザー作成フォーム */}
        {showCreateForm && (
          <UserForm
            onSave={handleCreateUser}
            onCancel={() => setShowCreateForm(false)}
            loading={saveLoading}
          />
        )}

        {/* ユーザー編集フォーム */}
        {editingUser && (
          <UserForm
            user={editingUser}
            onSave={handleUpdateUser}
            onCancel={() => setEditingUser(null)}
            loading={saveLoading}
          />
        )}

        {/* エラー表示 */}
        {error && (
          <div style={{
            backgroundColor: '#fee',
            color: '#c33',
            padding: '10px',
            borderRadius: '6px',
            marginTop: '20px',
            border: '1px solid #fcc'
          }}>
            {error}
            <button
              onClick={() => setError('')}
              style={{
                float: 'right',
                background: 'none',
                border: 'none',
                color: '#c33',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              ×
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsersManagement;